import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import "./CityWiseLead.css";
import API_BASE_URL from "./.././../config/env";

interface CityStats {
  id: number;
  city: string;
  total: number;
  active: number;
  invalid: number;
  not_q: number;
  site_visit: number;
  f2f: number;
  scheduled: number;
  token_done: number;
  transactions: number;
}

interface ApiResponse {
  status: string;
  user_count: number;
  total_lead: number;
  scheduled: number;
  f2f_count: number;
  site_visit_count: number;
  token_done_count: number;
  transaction_done_count: number;
  invalid_count: number;
  city_wise_data: CityStats[];
}

interface GPCUser {
  id: number;
  name: string;
  user_zones: string;
  role_id: number;
  pid: number;
  hasChild: number;
  attendance_id: number | null;
}

const CityWiseLead: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPartner, setSelectedPartner] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [gpcUsers, setGpcUsers] = useState<GPCUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    fetchGPCUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch city-wise data with user_id parameter
  const fetchCityWiseData = async (userId: string) => {
    if (!userId) {
      console.warn("No user ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${API_BASE_URL}/api/partner-count/leads/city-wise-data/${userId}`;

    try {
      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          timeout: 30000,
        }
      );

      if (response.data.status === "success") {
        setApiData(response.data);
      } else {
        setError(response.data.message || "Failed to fetch data");
      }
    } catch (err: any) {
      console.error("Error fetching city-wise data:", err);
      
      let errorMessage = "Failed to fetch city-wise data";
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = `API endpoint not found. Please check the URL: ${url}`;
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to access this data.";
        } else {
          errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchGPCUsers = async () => {
    if (!token) {
      console.warn("No token found for fetching GPC users");
      setError("Authentication token not found. Please login again.");
      setLoading(false);
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/partner-count/gpc-users-over-target`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 15000,
        }
      );

      if (response.data.status === "success" && response.data.data) {
        setGpcUsers(response.data.data);
        if (response.data.data.length > 0) {
          const defaultUser = response.data.data.find((user: GPCUser) => user.id === 1161) || response.data.data[0];
          const defaultUserId = String(defaultUser.id);
          setSelectedPartner(defaultUserId);
          setSearchTerm(defaultUser.name);
          fetchCityWiseData(defaultUserId);
        } else {
          setError("No users found");
          setLoading(false);
        }
      } else {
        console.warn("Failed to fetch GPC users:", response.data.message);
        setError(response.data.message || "Failed to fetch users");
        setLoading(false);
      }
    } catch (err: any) {
      console.error("Error fetching GPC users:", err);
      
      let errorMessage = "Failed to fetch users";
      if (err.response) {
        errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      }
      setError(errorMessage);
      setLoading(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle partner selection
  const handlePartnerSelect = (userId: string, userName: string) => {
    setSelectedPartner(userId);
    setSearchTerm(userName);
    setIsDropdownOpen(false);
    fetchCityWiseData(userId);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  // Filter users based on search term
  const getFilteredUsers = () => {
    if (!searchTerm.trim()) {
      return gpcUsers;
    }
    return gpcUsers.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getStatusCards = () => {
    if (!apiData) return [];
    
    return [
      { label: "Total Members", value: apiData.user_count, color: "#4A90D9" },
      { label: "Total Leads", value: apiData.total_lead, color: "#27AE60" },
      { label: "Scheduled", value: apiData.scheduled, color: "#F39C12" },
      { label: "F2F Done", value: apiData.f2f_count, color: "#8E44AD" },
      { label: "Site Visit", value: apiData.site_visit_count, color: "#1ABC9C" },
      { label: "Token Done", value: apiData.token_done_count, color: "#E67E22" },
      { label: "Transactions", value: apiData.transaction_done_count, color: "#2ECC71" },
      { label: "Invalid", value: apiData.invalid_count, color: "#E74C3C" },
    ];
  };

  const getCityData = () => {
    if (!apiData) return [];
    return apiData.city_wise_data;
  };

  const getValueColor = (value: number) => {
    if (value >= 25) return "#E74C3C";
    if (value >= 18) return "#F39C12";
    if (value >= 10) return "#3498DB";
    if (value >= 5) return "#27AE60";
    return "#95A5A6";
  };

  const getRowColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#E8F5E9',
      'Mumbai': '#FFF3E0',
      'Bengaluru': '#E3F2FD',
      'Dubai': '#FCE4EC',
      'Global Partners Connect': '#F3E5F5',
      'Global Property Expo': '#E0F7FA',
      'Propex': '#FFF8E1',
    };
    return colors[city] || '#FFFFFF';
  };

  const getRowHoverColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#C8E6C9',
      'Mumbai': '#FFE0B2',
      'Bengaluru': '#BBDEFB',
      'Dubai': '#F8BBD0',
      'Global Partners Connect': '#E1BEE7',
      'Global Property Expo': '#B2EBF2',
      'Propex': '#FFECB3',
    };
    return colors[city] || '#F5F5F5';
  };

  const getRowBorderColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#4CAF50',
      'Mumbai': '#FF9800',
      'Bengaluru': '#2196F3',
      'Dubai': '#E91E63',
      'Global Partners Connect': '#9C27B0',
      'Global Property Expo': '#00BCD4',
      'Propex': '#FFC107',
    };
    return colors[city] || '#DDDDDD';
  };

  // Handle city name click to navigate to lead details
  const handleCityClick = (cityName: string) => {
    if (selectedPartner) {
      navigate(`/admin-leads/${selectedPartner}?city=${encodeURIComponent(cityName)}`);
    }
  };

  // Handle value circle click
  const handleValueClick = (cityName: string, type: string) => {
    if (selectedPartner) {
      navigate(`/admin-leads/${selectedPartner}?type=${type}&city=${encodeURIComponent(cityName)}`);
    }
  };

  const retryFetch = () => {
    fetchGPCUsers();
  };

  // const toggleSidebar = () => {
  //   setSidebarCollapsed(!sidebarCollapsed);
  // };

  const isPageLoading = loadingUsers || loading;

  return (
    <div className="city-wise-lead-page">
      <Sidebar />
      
      <div className={`city-wise-lead-container ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="header-section">
          <h2>City-wise Leads Overview</h2>
          <p className="subtitle">Monitor leads by city and location</p>
        </div>

        {/* Filter Section with Searchable Dropdown */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-col">
              <label className="filter-label">Select Partner</label>
              <div className="searchable-dropdown" ref={dropdownRef}>
                <div className="dropdown-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search and select partner..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="search-input"
                    disabled={loadingUsers || loading}
                  />
                  <span className="dropdown-arrow" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    {isDropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-list">
                    {loadingUsers ? (
                      <div className="dropdown-loading">Loading partners...</div>
                    ) : getFilteredUsers().length > 0 ? (
                      getFilteredUsers().map((user) => (
                        <div
                          key={user.id}
                          className={`dropdown-item ${selectedPartner === String(user.id) ? 'selected' : ''}`}
                          onClick={() => handlePartnerSelect(String(user.id), user.name)}
                        >
                          <span className="user-name">{user.name}</span>
                          {selectedPartner === String(user.id) && (
                            <span className="check-mark">✓</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-no-results">No partners found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isPageLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{loadingUsers ? "Loading partners..." : "Loading city-wise data..."}</p>
          </div>
        )}

        {/* Error State */}
        {error && !isPageLoading && (
          <div className="error-state">
            <span className="error-icon">⚠️</span>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={retryFetch} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Data Display */}
        {!isPageLoading && !error && apiData && (
          <>
            <div className="summary-section">
              <h3>Overall Status Summary</h3>
              <div className="status-cards">
                {getStatusCards().map((card, index) => (
                  <div
                    key={index}
                    className="status-card"
                    style={{ borderLeftColor: card.color }}
                  >
                    <span className="status-value">{card.value}</span>
                    <span className="status-label">{card.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="table-section">
              <h3>City-wise Leads Distribution</h3>
              <div className="table-wrapper">
                <table className="city-table">
                  <thead>
                    <tr>
                      <th>CITY</th>
                      <th>TOTAL</th>
                      <th>ACTIVE</th>
                      <th>INVALID</th>
                      <th>NOT Q</th>
                      <th>SITE V</th>
                      <th>F2F</th>
                      <th>SCHED</th>
                      <th>TOKEN</th>
                      <th>TRANS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCityData().map((city, index) => (
                      <tr 
                        key={index}
                        className="city-row"
                        style={{
                          backgroundColor: getRowColor(city.city),
                          borderLeft: `4px solid ${getRowBorderColor(city.city)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getRowHoverColor(city.city);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = getRowColor(city.city);
                        }}
                      >
                        <td 
                          className="city-name clickable"
                          onClick={() => handleCityClick(city.city)}
                        >
                          <span>{city.city}</span>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.total) }}
                            onClick={() => handleValueClick(city.city, 'total')}
                          >
                            {city.total}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.active) }}
                            onClick={() => handleValueClick(city.city, 'active')}
                          >
                            {city.active}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.invalid) }}
                            onClick={() => handleValueClick(city.city, 'invalid')}
                          >
                            {city.invalid}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.not_q) }}
                            onClick={() => handleValueClick(city.city, 'not_q')}
                          >
                            {city.not_q}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.site_visit) }}
                            onClick={() => handleValueClick(city.city, 'site_visit')}
                          >
                            {city.site_visit}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.f2f) }}
                            onClick={() => handleValueClick(city.city, 'f2f')}
                          >
                            {city.f2f}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.scheduled) }}
                            onClick={() => handleValueClick(city.city, 'scheduled')}
                          >
                            {city.scheduled}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.token_done) }}
                            onClick={() => handleValueClick(city.city, 'token_done')}
                          >
                            {city.token_done}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="value-circle clickable" 
                            style={{ backgroundColor: getValueColor(city.transactions) }}
                            onClick={() => handleValueClick(city.city, 'transaction')}
                          >
                            {city.transactions}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {getCityData().length === 0 && (
                <div className="no-data">
                  <p>No city-wise data available</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CityWiseLead;